import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Subsubtype,
  SubSubTypeResponse,
} from '../../types/ServiceAccordion.type';
import {
  ConfirmationTemplate,
  normalizeConfirmationTemplate,
} from '../../types/RegistrationConfirmation.type';
import { CustomerIntakeFormConfig } from '../../types/CustomerIntake.type';
import { z } from 'zod';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import { apiPost, apiPut } from '../../utils/apiClient';
import { useIsMobile } from '../../hooks/useResponsive';
import ConfirmationTemplateEditor from './ConfirmationTemplateEditor';
import CustomerIntakeDefinitionEditor, {
  applyPackageNameDefaults,
  createEmptyPackageFormConfiguration,
} from '../CustomerIntake/CustomerIntakeDefinitionEditor';
import { getSubSubtypeFormConfiguration } from '../../utils/serviceFormConfigurationApi';

type SubSubTypeDialogProps = {
  open: boolean;
  handleClose: () => void;
  subsubtype: Subsubtype | null;
  subtypeId: string;
  fetchServiceTypes: () => void;
};

const SubSubtypeInput = z.object({
  id: z.number().int().optional(),
  name: z.string().min(1, 'Package name is required'),
  invoiceName: z.string().optional(),
  invoiceNameForChild: z.string().optional(),
  subtypeId: z.number().int().optional(),
  serviceFor: z.string(),
  price: z.number().min(0, 'Price must be a positive number'),
  priceForChild: z
    .number()
    .min(0, 'Price for child must be a positive number')
    .default(0),
  vatRate: z
    .number()
    .min(0, 'VAT rate must be a positive number')
    .max(100, 'VAT rate cannot exceed 100%')
    .default(0),
  unitMeasure: z.string().optional(),
  duration: z
    .number()
    .min(1, 'Duration must be must be a positive number')
    .default(1),
  visitType: z.string(),
  visitBalance: z
    .number()
    .min(1, 'Visit balance must be must be a positive number')
    .default(1),
});
type SubSubtypeInputType = z.infer<typeof SubSubtypeInput>;
type SubSubtypePayload = SubSubtypeInputType & {
  confirmationTemplate: ConfirmationTemplate;
  formConfiguration?: CustomerIntakeFormConfig & { schemaVersion: 1 };
};
type FormError = {
  name?: string;
  serviceFor?: string;
  price?: string;
  duration?: string;
  visitType?: string;
  visitBalance?: string;
  invoiceName?: string;
  invoiceNameForChild?: string;
  vatRate?: string;
  unitMeasure?: string;
  priceForChild?: string;
};

const SubSubTypeDialog = ({
  open,
  handleClose,
  subsubtype,
  subtypeId,
  fetchServiceTypes,
}: SubSubTypeDialogProps) => {
  const isMobile = useIsMobile();
  const [tab, setTab] = React.useState(0);
  const [formError, setFormError] = React.useState<FormError>({});
  const [userType, setUserType] = React.useState<string>(
    subsubtype?.serviceFor || '',
  );
  const [packageName, setPackageName] = React.useState(subsubtype?.name || '');
  const packageNameRef = React.useRef(packageName);
  const [confirmationTemplate, setConfirmationTemplate] =
    React.useState<ConfirmationTemplate>(() =>
      normalizeConfirmationTemplate(subsubtype?.confirmationTemplate),
    );
  const [formConfiguration, setFormConfiguration] =
    React.useState<CustomerIntakeFormConfig>(() =>
      createEmptyPackageFormConfiguration(subsubtype?.name),
    );
  const [formConfigurationLoading, setFormConfigurationLoading] =
    React.useState(false);
  const [formConfigurationError, setFormConfigurationError] =
    React.useState('');
  const [formConfigurationDirty, setFormConfigurationDirty] =
    React.useState(false);
  const [formConfigurationRetry, setFormConfigurationRetry] = React.useState(0);

  React.useEffect(() => {
    setTab(0);
    setUserType(subsubtype?.serviceFor || '');
    const initialPackageName = subsubtype?.name || '';
    packageNameRef.current = initialPackageName;
    setPackageName(initialPackageName);
    setConfirmationTemplate(
      normalizeConfirmationTemplate(subsubtype?.confirmationTemplate),
    );
    setFormConfiguration(
      createEmptyPackageFormConfiguration(initialPackageName),
    );
    setFormConfigurationError('');
    setFormConfigurationDirty(false);

    if (!open || !subsubtype) {
      setFormConfigurationLoading(false);
      return;
    }

    let active = true;
    setFormConfigurationLoading(true);
    void getSubSubtypeFormConfiguration(subsubtype.id)
      .then((configuration) => {
        if (!active) return;
        const currentPackageName = packageNameRef.current;
        setFormConfiguration(
          configuration
            ? applyPackageNameDefaults(
                configuration,
                currentPackageName,
                initialPackageName,
              )
            : createEmptyPackageFormConfiguration(currentPackageName),
        );
        setFormConfigurationError('');
        setFormConfigurationDirty(false);
      })
      .catch((error) => {
        if (!active) return;
        setFormConfigurationError(
          error instanceof Error
            ? error.message
            : 'Unable to load package form configuration.',
        );
      })
      .finally(() => {
        if (active) setFormConfigurationLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, subsubtype, formConfigurationRetry]);

  const hasServiceErrors = Object.keys(formError).length > 0;

  const handleConfirm = async (data: SubSubtypePayload) => {
    try {
      const payload: SubSubtypePayload = {
        ...data,
        ...(subsubtype
          ? formConfigurationDirty
            ? {
                formConfiguration: {
                  schemaVersion: 1,
                  ...formConfiguration,
                },
              }
            : {}
          : {
              formConfiguration: {
                schemaVersion: 1,
                ...formConfiguration,
              },
            }),
      };

      if (subsubtype) {
        // Update existing package
        const updateRes = await apiPut<SubSubTypeResponse, SubSubtypePayload>(
          '/service/sub-subtype/update',
          payload,
        );
        notifyAPIClient(updateRes.message);
      } else {
        // Create new package
        const createRes = await apiPost<SubSubTypeResponse, SubSubtypePayload>(
          '/service/sub-subtype/create',
          {
            ...payload,
            subtypeId: Number(subtypeId),
          },
        );
        notifyAPIClient(createRes.message);
      }
      fetchServiceTypes();
      setUserType('');
      handleClose();
    } catch (error) {
      notifyFailure();
    }
  };

  // Parse + validate the service-information fields. Returns the parsed data, or
  // null after setting field errors.
  const parseBasicInfo = (
    form: HTMLFormElement,
  ): SubSubtypeInputType | null => {
    const formData = new FormData(form);

    try {
      const data = SubSubtypeInput.parse({
        id: subsubtype?.id || 0,
        name: formData.get('name'),
        invoiceName: formData.get('invoiceName'),
        invoiceNameForChild:
          formData.get('serviceFor') === 'GROUP'
            ? formData.get('invoiceNameForChild')
            : '',
        subtypeId: Number(subtypeId),
        serviceFor: formData.get('serviceFor'),
        price: Number(formData.get('price')),
        priceForChild:
          formData.get('serviceFor') === 'GROUP'
            ? Number(formData.get('priceForChild'))
            : 0,
        vatRate: Number(formData.get('vatRate')),
        unitMeasure: formData.get('unitMeasure'),
        duration: Number(formData.get('duration')),
        visitType: formData.get('visitType'),
        visitBalance: Number(formData.get('visitBalance')),
      });

      setFormError({});
      return data;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormError = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormError;
          fieldErrors[field] = err.message;
        });
        setFormError(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
      return null;
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = parseBasicInfo(event.currentTarget);
    if (!data) {
      // Validation errors only occur on the service tab — surface it.
      setTab(0);
      return;
    }
    handleConfirm({ ...data, confirmationTemplate });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'calc(100vh - 32px)',
        },
      }}
      open={open}
      onClose={() => {
        setFormError({});
        setUserType('');
        setTab(0);
        handleClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: '1 1 auto',
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          {subsubtype ? 'Edit package' : 'Add new package'}
        </DialogTitle>

        {/* Independent tabs — the two panels do not depend on each other, so the
            user can switch freely; both stay mounted (display toggle) to keep the
            uncontrolled service fields in the submitted FormData. */}
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Service information"
            sx={{ color: hasServiceErrors ? 'error.main' : undefined }}
          />
          <Tab label="Confirmation content" />
          <Tab label="Form configuration" />
        </Tabs>

        <DialogContent
          sx={{
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {/* Service basic information — a responsive 2-column grid of
              outlined fields, matching the confirmation tab's field style. */}
          <Box
            sx={{
              display: tab === 0 ? 'grid' : 'none',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              pt: 1,
            }}
          >
            <TextField
              autoFocus
              id="name"
              name="name"
              label="Package Name"
              required
              type="text"
              fullWidth
              size="small"
              value={packageName}
              onChange={(event) => {
                const nextPackageName = event.target.value;
                const previousPackageName = packageNameRef.current;
                packageNameRef.current = nextPackageName;
                setPackageName(nextPackageName);
                setFormConfiguration((current) =>
                  applyPackageNameDefaults(
                    current,
                    nextPackageName,
                    previousPackageName,
                  ),
                );
                setFormConfigurationDirty(true);
              }}
              error={Boolean(formError.name)}
              helperText={formError.name}
              sx={{ gridColumn: '1 / -1' }}
            />

            <TextField
              id="invoiceName"
              name="invoiceName"
              label="Invoice Name"
              type="text"
              fullWidth
              size="small"
              defaultValue={subsubtype?.invoiceName || ''}
              error={Boolean(formError.invoiceName)}
              helperText={formError.invoiceName}
            />

            <FormControl
              fullWidth
              size="small"
              error={Boolean(formError.serviceFor)}
            >
              <InputLabel id="serviceFor-label">Service For</InputLabel>
              <Select
                labelId="serviceFor-label"
                id="serviceFor"
                name="serviceFor"
                label="Service For"
                defaultValue={subsubtype?.serviceFor || ''}
                onChange={(e) => setUserType(e.target.value as string)}
              >
                <MenuItem value="ADULT">Adult</MenuItem>
                <MenuItem value="CHILD">Child</MenuItem>
                <MenuItem value="GROUP">Group</MenuItem>
              </Select>
              {formError.serviceFor && (
                <FormHelperText>{formError.serviceFor}</FormHelperText>
              )}
            </FormControl>

            {(userType === 'GROUP' ||
              (userType === '' && subsubtype?.serviceFor === 'GROUP')) && (
              <TextField
                id="invoiceNameForChild"
                name="invoiceNameForChild"
                label="Invoice Name for Child"
                type="text"
                fullWidth
                size="small"
                defaultValue={subsubtype?.invoiceNameForChild || ''}
                error={Boolean(formError.invoiceNameForChild)}
                helperText={formError.invoiceNameForChild}
                sx={{ gridColumn: '1 / -1' }}
              />
            )}

            <TextField
              id="price"
              name="price"
              label="Price"
              required
              type="number"
              fullWidth
              size="small"
              defaultValue={subsubtype?.price || ''}
              error={Boolean(formError.price)}
              helperText={formError.price}
            />

            {(userType === 'GROUP' ||
              (userType === '' && subsubtype?.serviceFor === 'GROUP')) && (
              <TextField
                id="priceForChild"
                name="priceForChild"
                label="Price for Child"
                type="number"
                fullWidth
                size="small"
                defaultValue={subsubtype?.priceForChild || '0'}
                error={Boolean(formError.priceForChild)}
                helperText={formError.priceForChild}
              />
            )}

            <TextField
              id="vatRate"
              name="vatRate"
              label="VAT Rate (%)"
              required
              type="number"
              fullWidth
              size="small"
              defaultValue={subsubtype?.vatRate || ''}
              error={Boolean(formError.vatRate)}
              helperText={formError.vatRate}
            />

            <TextField
              id="unitMeasure"
              name="unitMeasure"
              label="Unit Measure"
              type="text"
              fullWidth
              size="small"
              defaultValue={subsubtype?.unitMeasure || ''}
              error={Boolean(formError.unitMeasure)}
              helperText={formError.unitMeasure}
            />

            <TextField
              id="duration"
              name="duration"
              label="Duration (days)"
              required
              type="number"
              fullWidth
              size="small"
              defaultValue={subsubtype?.duration || ''}
              error={Boolean(formError.duration)}
              helperText={formError.duration}
            />

            <FormControl
              fullWidth
              size="small"
              error={Boolean(formError.visitType)}
            >
              <InputLabel id="visitType-label">Visit Type</InputLabel>
              <Select
                labelId="visitType-label"
                id="visitType"
                name="visitType"
                label="Visit Type"
                defaultValue={subsubtype?.visitType || ''}
              >
                <MenuItem value="count">Count</MenuItem>
                <MenuItem value="unlimited">Unlimited</MenuItem>
              </Select>
              {formError.visitType && (
                <FormHelperText>{formError.visitType}</FormHelperText>
              )}
            </FormControl>

            <TextField
              id="visitBalance"
              name="visitBalance"
              label="Visit Balance"
              required
              type="number"
              fullWidth
              size="small"
              defaultValue={subsubtype?.visitBalance || ''}
              error={Boolean(formError.visitBalance)}
              helperText={formError.visitBalance}
            />
          </Box>

          <Box sx={{ display: tab === 1 ? 'block' : 'none', pt: 1 }}>
            <ConfirmationTemplateEditor
              value={confirmationTemplate}
              onChange={setConfirmationTemplate}
              embedded
            />
          </Box>

          <Box sx={{ display: tab === 2 ? 'block' : 'none', pt: 1 }}>
            {formConfigurationLoading ? (
              <FormHelperText>
                Loading package form configuration...
              </FormHelperText>
            ) : formConfigurationError ? (
              <Box>
                <FormHelperText error>{formConfigurationError}</FormHelperText>
                <Button
                  type="button"
                  size="small"
                  onClick={() =>
                    setFormConfigurationRetry((current) => current + 1)
                  }
                  sx={{ mt: 1 }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <CustomerIntakeDefinitionEditor
                value={formConfiguration}
                mode="package"
                onChange={(nextConfiguration) => {
                  setFormConfiguration(nextConfiguration);
                  setFormConfigurationDirty(true);
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              handleClose();
              setFormError({});
              setUserType('');
              setTab(0);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubSubTypeDialog;
